/**
 * Prior 1.0 activity of a migrated member.
 *
 * How a Pelago 1.0 member engaged before their 1.0 freeze (the migration's
 * Tier 0 start), so behavioural analytics can compare a member's engagement
 * after migrating against what it was before. An active day is a member-local
 * day with a member-authored check-in or a 1.0 chat message the member sent.
 * What leaves the database is one closed class and three day counts between 0
 * and 28: never a date, an instant or an identifier.
 *
 * A check-in day is one distinct stored day sentinel of a member-authored
 * check-in (the writer decides the member-local day at the source). A chat day
 * is the member-local date of a sent message, in the zone in effect when it
 * was sent, so the two kinds of day are the same kind of date and the active
 * days are their union, not their sum.
 */

import { DateTime } from 'luxon';
import { z } from 'zod';

/** Closed vocabulary: how the member engaged before the freeze. */
export const PRIOR_ACTIVITY_VALUES = ['active', 'returning', 'no_history'] as const;
export const priorActivitySchema = z.enum(PRIOR_ACTIVITY_VALUES);
export type PriorActivity = z.infer<typeof priorActivitySchema>;

/** Person-property keys as they appear in the analytics store. */
export const PRIOR_CHECKIN_ACTIVITY_PERSON_PROPERTY_KEYS = {
  priorActivity: 'prior_activity',
  priorCheckinDays28d: 'prior_checkin_days_28d',
  priorCheckinCoveredDays28d: 'prior_checkin_covered_days_28d',
  priorChatDays28d: 'prior_chat_days_28d',
  priorActiveDays28d: 'prior_active_days_28d',
  priorCheckinDays7d: 'prior_checkin_days_7d',
  priorCheckinCoveredDays7d: 'prior_checkin_covered_days_7d',
  priorChatDays7d: 'prior_chat_days_7d',
  priorActiveDays7d: 'prior_active_days_7d',
} as const;

/**
 * Person-property keys of the member's activity after the move, per window.
 * A window's three keys are only ever sent together, once the window has
 * closed; until then they are absent from the person.
 */
export const AFTER_MIGRATION_ACTIVITY_PERSON_PROPERTY_KEYS = {
  '7d': {
    checkinDays: 'after_checkin_days_7d',
    checkinCoveredDays: 'after_checkin_covered_days_7d',
    chatDays: 'after_chat_days_7d',
    activeDays: 'after_active_days_7d',
  },
  '28d': {
    checkinDays: 'after_checkin_days_28d',
    checkinCoveredDays: 'after_checkin_covered_days_28d',
    chatDays: 'after_chat_days_28d',
    activeDays: 'after_active_days_28d',
  },
} as const;

/** A member with a check-in day in this many days before the freeze was active. */
export const PRIOR_ACTIVE_WINDOW_DAYS = 183;
/** The recent window, in days: the 28-day counts and the 28-day check-in change. */
export const PRIOR_RECENT_WINDOW_DAYS = 28;
/**
 * How far before the freeze the recent-day read starts. Wider than the window
 * on purpose: the 28 member-local dates ending on the freeze's local date can
 * start more than 28 elapsed days before the freeze instant (a zone west of
 * UTC, a daylight-saving change), and the read must hold every row the date
 * filter could keep. The date filter, not this bound, decides what counts.
 */
const RECENT_DAYS_READ_MARGIN_DAYS = 2;

/**
 * Closed vocabulary: the check-in change windows, each compared as the same
 * number of days before the freeze and after the migration completed.
 */
export const CHECKIN_CHANGE_WINDOWS = ['7d', '28d'] as const;
export type CheckInChangeWindow = (typeof CHECKIN_CHANGE_WINDOWS)[number];
export const CHECKIN_CHANGE_WINDOW_DAYS: Readonly<Record<CheckInChangeWindow, number>> = {
  '7d': 7,
  '28d': PRIOR_RECENT_WINDOW_DAYS,
};

/**
 * Whether the window after completion has fully elapsed at `now`: only then is
 * an after-window count final, so a fresh migration never reads as a drop and
 * a partial count is never reported.
 */
export function checkInChangeWindowClosed(
  completedAt: Date,
  window: CheckInChangeWindow,
  now: Date,
): boolean {
  return (
    DateTime.fromJSDate(completedAt, { zone: 'utc' }).plus({
      days: CHECKIN_CHANGE_WINDOW_DAYS[window],
    }) <= DateTime.fromJSDate(now, { zone: 'utc' })
  );
}

/** Closed vocabulary: how check-in days after the migration compare with before it. */
export const CHECKIN_CHANGE_DIRECTIONS = ['up', 'same', 'down'] as const;
export type CheckInChangeDirection = (typeof CHECKIN_CHANGE_DIRECTIONS)[number];

export interface PriorCheckInWindows {
  /** The freeze instant; a prior check-in was recorded strictly before it. */
  readonly freezeAt: Date;
  /**
   * Exclusive bound on a prior check-in's stored day: one day past the freeze.
   * A day is stored as the UTC midnight of the member-local date, which runs
   * up to 14 hours ahead of the instant it was entered, so a check-in entered
   * just before the freeze can carry a day after it.
   */
  readonly priorDayEnd: Date;
  /** Inclusive start of the active window. */
  readonly activeWindowStart: Date;
  /** Inclusive start of the recent (28-day) window. */
  readonly recentWindowStart: Date;
  /** Inclusive start of the recent-day read, two days before the recent window. */
  readonly recentDaysReadStart: Date;
  /** Inclusive start of the 7 days before the freeze. */
  readonly weekBeforeFreezeStart: Date;
  /** The Tier 0 completion instant, the inclusive start of both after-windows; null when unrecorded. */
  readonly completedAt: Date | null;
  /** Exclusive end of the first 7 days after completion; null without a completion. */
  readonly firstWeekAfterEnd: Date | null;
  /** Exclusive end of the first 28 days after completion; null without a completion. */
  readonly first28DaysAfterEnd: Date | null;
}

/**
 * The window bounds for a freeze instant and, when known, a completion
 * instant, as instants. Computed in UTC so the result never depends on the
 * host zone: the windows are elapsed-day offsets from an instant, not
 * member-local calendar days.
 */
export function priorCheckInWindows(
  tier0StartedAt: Date,
  tier0CompletedAt: Date | null = null,
): PriorCheckInWindows {
  const freeze = DateTime.fromJSDate(tier0StartedAt, { zone: 'utc' });
  const completed =
    tier0CompletedAt === null ? null : DateTime.fromJSDate(tier0CompletedAt, { zone: 'utc' });
  return {
    freezeAt: freeze.toJSDate(),
    priorDayEnd: freeze.plus({ days: 1 }).toJSDate(),
    activeWindowStart: freeze.minus({ days: PRIOR_ACTIVE_WINDOW_DAYS }).toJSDate(),
    recentWindowStart: freeze.minus({ days: PRIOR_RECENT_WINDOW_DAYS }).toJSDate(),
    recentDaysReadStart: freeze
      .minus({ days: PRIOR_RECENT_WINDOW_DAYS + RECENT_DAYS_READ_MARGIN_DAYS })
      .toJSDate(),
    weekBeforeFreezeStart: freeze.minus({ days: CHECKIN_CHANGE_WINDOW_DAYS['7d'] }).toJSDate(),
    completedAt: completed?.toJSDate() ?? null,
    firstWeekAfterEnd:
      completed?.plus({ days: CHECKIN_CHANGE_WINDOW_DAYS['7d'] }).toJSDate() ?? null,
    first28DaysAfterEnd:
      completed?.plus({ days: CHECKIN_CHANGE_WINDOW_DAYS['28d'] }).toJSDate() ?? null,
  };
}

/**
 * What the class needs: distinct member check-in days recorded before the
 * freeze, overall and inside the active window (counted on the stored day),
 * and whether the member sent any imported 1.0 chat message before the freeze
 * and inside the active window, so the class can count chat days as active
 * days without the class read bucketing chat instants into dates.
 */
export interface PriorCheckInDayCounts {
  readonly memberChatBeforeFreeze: boolean;
  readonly memberChatInActiveWindow: boolean;
  readonly daysBeforeFreeze: number;
  readonly daysInActiveWindow: number;
}

/** The counts of a member with no check-in day at all. */
export const NO_PRIOR_CHECK_IN_DAYS: PriorCheckInDayCounts = {
  memberChatBeforeFreeze: false,
  memberChatInActiveWindow: false,
  daysBeforeFreeze: 0,
  daysInActiveWindow: 0,
};

/**
 * The class. `active` needs an active day inside the active window; a member
 * whose active days all predate it is `returning`; a member with no active
 * day before the freeze has `no_history`. An active day is a check-in day or
 * a day the member sent a 1.0 chat message.
 */
export function derivePriorActivity(
  counts: Pick<
    PriorCheckInDayCounts,
    | 'daysBeforeFreeze'
    | 'daysInActiveWindow'
    | 'memberChatBeforeFreeze'
    | 'memberChatInActiveWindow'
  >,
): PriorActivity {
  if (counts.daysInActiveWindow >= 1 || counts.memberChatInActiveWindow) return 'active';
  if (counts.daysBeforeFreeze >= 1 || counts.memberChatBeforeFreeze) return 'returning';
  return 'no_history';
}

/**
 * Distinct member-local days in a window. A check-in day is the day the member
 * acted: the member-local date on which they recorded a check-in, whatever day
 * it was for. A covered day is the day a check-in was for (its stored day), so
 * one submit that backfills a week is one check-in day and seven covered days.
 */
export interface PriorRecentDayCounts {
  /** Days on which the member recorded a member-authored check-in. */
  readonly checkInDays: number;
  /** Days a member-authored check-in was for (the stored day). */
  readonly coveredCheckInDays: number;
  /** Days on which the member sent a chat message. */
  readonly chatDays: number;
  /** Days with a recorded check-in or a sent chat message: the union, never the sum. */
  readonly activeDays: number;
}

/** The member's recent rows, as the recent-day read returns them. */
export interface PriorRecentActivityRows {
  /** Stored day sentinels (UTC midnight of the member-local date) of prior check-ins. */
  readonly checkInDays: readonly Date[];
  /** Instants at which the member recorded a prior check-in. */
  readonly checkInRecordedAt: readonly Date[];
  /** Send instants of imported 1.0 chat messages the member sent. */
  readonly memberChatSentAt: readonly Date[];
}

const isoDateOf = (at: Date, zone: string): string =>
  DateTime.fromJSDate(at, { zone }).toISODate() ?? '';

/** The member-local dates of `instants`, each read in the zone in effect at it. */
const localDatesOf = (instants: readonly Date[], zoneAt: (at: Date) => string): string[] =>
  instants.map((at) => isoDateOf(at, zoneAt(at)));

/**
 * Count the member's check-in, covered, chat and active days over the
 * `windowDays` (28 unless given) member-local dates that end on the freeze's
 * local date. `zoneAt` names the member's zone in effect at an instant; the
 * freeze date and every recorded check-in and chat date are read in it. A
 * covered day is already a member-local date, so it is read as stored. Only an
 * instant before the freeze counts, so every count is between 0 and the window
 * length.
 */
export function countPriorRecentDays(
  freezeAt: Date,
  rows: PriorRecentActivityRows,
  zoneAt: (at: Date) => string,
  windowDays: number = PRIOR_RECENT_WINDOW_DAYS,
): PriorRecentDayCounts {
  const freeze = DateTime.fromJSDate(freezeAt, { zone: 'utc' });
  const lastDate = isoDateOf(freezeAt, zoneAt(freezeAt));
  const firstDate =
    DateTime.fromISO(lastDate, { zone: 'utc' })
      .minus({ days: windowDays - 1 })
      .toISODate() ?? '';
  const inWindow = (date: string): boolean => date >= firstDate && date <= lastDate;
  const beforeFreeze = (at: Date): boolean => DateTime.fromJSDate(at, { zone: 'utc' }) < freeze;

  const checkInDates = new Set(
    localDatesOf(rows.checkInRecordedAt.filter(beforeFreeze), zoneAt).filter(inWindow),
  );
  const coveredDates = new Set(
    rows.checkInDays.map((day) => isoDateOf(day, 'utc')).filter(inWindow),
  );
  const chatDates = new Set(
    localDatesOf(rows.memberChatSentAt.filter(beforeFreeze), zoneAt).filter(inWindow),
  );
  return {
    checkInDays: checkInDates.size,
    coveredCheckInDays: coveredDates.size,
    chatDays: chatDates.size,
    activeDays: new Set([...checkInDates, ...chatDates]).size,
  };
}

/** No recent row at all. */
export const NO_PRIOR_RECENT_DAYS: PriorRecentDayCounts = {
  checkInDays: 0,
  coveredCheckInDays: 0,
  chatDays: 0,
  activeDays: 0,
};

/**
 * The member's rows after the move, as the after-window read returns them:
 * check-in day sentinels already inside the 28-day covered window, the
 * instants the member recorded a check-in in the first 28 days, and send
 * instants of native care-team chat messages the member sent in them.
 */
export interface AfterWindowActivityRows {
  /** Stored day sentinels (UTC midnight of the member-local date) of after-window check-ins. */
  readonly checkInDays: readonly Date[];
  /** Instants at which the member recorded a check-in after completion. */
  readonly checkInRecordedAt: readonly Date[];
  /** Send instants of native care-team chat messages the member sent. */
  readonly memberChatSentAt: readonly Date[];
}

/** Check-in, covered, chat and active days in one window after the move. */
export type AfterWindowDayCounts = PriorRecentDayCounts;

/**
 * Count the member's days in the first `window` after Tier 0 completion. A
 * check-in or chat day is the member-local date (in the zone in effect at the
 * instant) of a check-in recorded or a message sent in [completion, completion
 * + N days); an elapsed window can touch N + 1 local dates. A covered day is a
 * stored day whose sentinel (UTC midnight of the date) falls in that range:
 * exactly the days the count statement counts after completion. Active days
 * are the union of check-in and chat days.
 */
export function countAfterWindowDays(
  completedAt: Date,
  window: CheckInChangeWindow,
  rows: AfterWindowActivityRows,
  zoneAt: (at: Date) => string,
): AfterWindowDayCounts {
  const start = DateTime.fromJSDate(completedAt, { zone: 'utc' });
  const end = start.plus({ days: CHECKIN_CHANGE_WINDOW_DAYS[window] });
  const inRange = (at: DateTime): boolean => at >= start && at < end;
  const atInRange = (at: Date): boolean => inRange(DateTime.fromJSDate(at, { zone: 'utc' }));

  const checkInDates = new Set(localDatesOf(rows.checkInRecordedAt.filter(atInRange), zoneAt));
  const coveredDates = new Set(
    rows.checkInDays
      .map((day) => isoDateOf(day, 'utc'))
      .filter((date) => inRange(DateTime.fromISO(date, { zone: 'utc' }))),
  );
  const chatDates = new Set(localDatesOf(rows.memberChatSentAt.filter(atInRange), zoneAt));
  return {
    checkInDays: checkInDates.size,
    coveredCheckInDays: coveredDates.size,
    chatDays: chatDates.size,
    activeDays: new Set([...checkInDates, ...chatDates]).size,
  };
}

/**
 * Whether the member checked in on more, the same or fewer days in the first
 * window after completing than in the same-length window ending on the freeze
 * date. Both sides count check-in days (the days the member acted), so the
 * gauge and the person properties compare the same numbers.
 */
export function checkInChangeDirection(
  before: Pick<PriorRecentDayCounts, 'checkInDays'>,
  after: Pick<AfterWindowDayCounts, 'checkInDays'>,
): CheckInChangeDirection {
  if (after.checkInDays > before.checkInDays) return 'up';
  if (after.checkInDays < before.checkInDays) return 'down';
  return 'same';
}

const AFTER_7D = AFTER_MIGRATION_ACTIVITY_PERSON_PROPERTY_KEYS['7d'];
const AFTER_28D = AFTER_MIGRATION_ACTIVITY_PERSON_PROPERTY_KEYS['28d'];
const PRIOR_KEYS = PRIOR_CHECKIN_ACTIVITY_PERSON_PROPERTY_KEYS;

/** The 7-day prior counts under their keys. */
export type PriorWeekPersonProperties = {
  readonly [PRIOR_KEYS.priorCheckinDays7d]: number;
  readonly [PRIOR_KEYS.priorCheckinCoveredDays7d]: number;
  readonly [PRIOR_KEYS.priorChatDays7d]: number;
  readonly [PRIOR_KEYS.priorActiveDays7d]: number;
};

/** The first 7 days after the move under their keys. */
export type AfterWeekPersonProperties = {
  readonly [AFTER_7D.checkinDays]: number;
  readonly [AFTER_7D.checkinCoveredDays]: number;
  readonly [AFTER_7D.chatDays]: number;
  readonly [AFTER_7D.activeDays]: number;
};

/** The first 28 days after the move under their keys. */
export type After28DaysPersonProperties = {
  readonly [AFTER_28D.checkinDays]: number;
  readonly [AFTER_28D.checkinCoveredDays]: number;
  readonly [AFTER_28D.chatDays]: number;
  readonly [AFTER_28D.activeDays]: number;
};

export type PriorActivityPersonProperties = {
  readonly [PRIOR_KEYS.priorActivity]: PriorActivity;
  readonly [PRIOR_KEYS.priorCheckinDays28d]: number;
  readonly [PRIOR_KEYS.priorCheckinCoveredDays28d]: number;
  readonly [PRIOR_KEYS.priorChatDays28d]: number;
  readonly [PRIOR_KEYS.priorActiveDays28d]: number;
} & Partial<PriorWeekPersonProperties> &
  Partial<AfterWeekPersonProperties> &
  Partial<After28DaysPersonProperties>;

/**
 * The class and the day counts under their person-property keys, the counts
 * as numbers. The 7-day prior counts are sent when supplied; an after window
 * is sent only when its counts are supplied, which the caller does only once
 * that window has closed.
 */
export function priorActivityPersonProperties(
  priorActivity: PriorActivity,
  days: PriorRecentDayCounts,
  extra: {
    readonly priorWeek?: PriorRecentDayCounts;
    readonly after?: Partial<Record<CheckInChangeWindow, AfterWindowDayCounts>>;
  } = {},
): PriorActivityPersonProperties {
  const keys = PRIOR_CHECKIN_ACTIVITY_PERSON_PROPERTY_KEYS;
  const week = extra.after?.['7d'];
  const month = extra.after?.['28d'];
  return {
    [keys.priorActivity]: priorActivity,
    [keys.priorCheckinDays28d]: days.checkInDays,
    [keys.priorCheckinCoveredDays28d]: days.coveredCheckInDays,
    [keys.priorChatDays28d]: days.chatDays,
    [keys.priorActiveDays28d]: days.activeDays,
    ...(extra.priorWeek !== undefined && {
      [keys.priorCheckinDays7d]: extra.priorWeek.checkInDays,
      [keys.priorCheckinCoveredDays7d]: extra.priorWeek.coveredCheckInDays,
      [keys.priorChatDays7d]: extra.priorWeek.chatDays,
      [keys.priorActiveDays7d]: extra.priorWeek.activeDays,
    }),
    ...(week !== undefined && {
      [AFTER_7D.checkinDays]: week.checkInDays,
      [AFTER_7D.checkinCoveredDays]: week.coveredCheckInDays,
      [AFTER_7D.chatDays]: week.chatDays,
      [AFTER_7D.activeDays]: week.activeDays,
    }),
    ...(month !== undefined && {
      [AFTER_28D.checkinDays]: month.checkInDays,
      [AFTER_28D.checkinCoveredDays]: month.coveredCheckInDays,
      [AFTER_28D.chatDays]: month.chatDays,
      [AFTER_28D.activeDays]: month.activeDays,
    }),
  };
}
